import axios from "axios";
import dayjs from "dayjs";
import jwtDecode from "jwt-decode";

import configurations from "commons/configurations";
import {
  iniciarNotificacoes,
  pararNotificacoes
} from "services/signalr.service";


// mutations (privadas)
const MUTATE_TOKEN = "mutateToken";
const MUTATE_USER = "mutateUser";
const MUTATE_MENU = "mutateMenu";
const MUTATE_ABILITIES = "mutateAbilities";
const MUTATE_TEM_NOTIFICACAO = "mutateTemNotificacao"
let autoRefreshTimeoutId = null;

// actions (publicas)
export const AUTH_NAMESPACE = "auth";
export const LOGIN = "login";
export const LOGOUT = "sair";
export const REFRESH_TOKEN = "refreshToken";
export const AUTO_REFRESH_TOKEN = "autoRefreshToken";
export const FORCE_LOGOUT = "forceLogout";
export const UPDATE_MENU = "updateMenu";
export const UPDATE_ABILITIES = "updateAbilities";
export const CLEAR = "clear";
export const SETAR_NOTIFICACAO = "setarNotificacao"
export const LIMPAR_NOTIFICACAO = "limparNotificacao"

// getters (publicos)
export const TOKEN = "see.avp.token";
export const USER = "user";
export const MENU = "menu";
export const ABILITIES = "abilities";
export const TEM_NOTIFICACAO = 'temNotificacao'


function getDecodedItem(key, defaultValue = null) {
  const item = localStorage.getItem(key);
  if (!item) {
    return defaultValue;
  }
  try {
    // Tenta decodificar de Base64 e depois fazer o parse do JSON
    return JSON.parse(atob(item));
  } catch (error) {
    // Se falhar, o dado está corrompido ou não é Base64/JSON.
    console.warn(`Falha ao carregar o item '${key}' do localStorage.`, error);
    localStorage.removeItem(key); // Remove o item inválido para evitar erros futuros
    return defaultValue;
  }
}

function clearAutoRefreshTimer() {
  if (autoRefreshTimeoutId) {
    clearTimeout(autoRefreshTimeoutId);
    autoRefreshTimeoutId = null;
  }
}

function redirectToLogin() {
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

export default {
  namespaced: true,
  state: () => ({
    token: localStorage.getItem(TOKEN) || "",
    user: getDecodedItem(USER, null),
    menu: getDecodedItem(MENU, null),
    abilities: getDecodedItem(ABILITIES, null),
    temNotificacao: getDecodedItem(TEM_NOTIFICACAO, null),
  }),
  mutations: {
    [MUTATE_TOKEN](state, token) {
      state.token = token;
      if (token) {
        localStorage.setItem(TOKEN, token || "");
      } else {
        localStorage.removeItem(TOKEN);
      }
    },
    [MUTATE_USER](state, user) {
      state.user = user;
      if (user) {
        localStorage.setItem(USER, btoa(JSON.stringify(user)));
      } else {
        localStorage.removeItem(USER);
      }
    },
    [MUTATE_MENU](state, menu) {
      state.menu = menu;
      if (menu) {
        localStorage.setItem(MENU, btoa(JSON.stringify(menu)));
      } else {
        localStorage.removeItem(MENU);
      }
    },
    [MUTATE_ABILITIES](state, abilities) {
      state.abilities = abilities;
      if (abilities) {
        localStorage.setItem(ABILITIES, btoa(JSON.stringify(abilities)));
      } else {
        localStorage.removeItem(ABILITIES);
      }
    },
    [MUTATE_TEM_NOTIFICACAO](state, value) {
      state.temNotificacao = value;
      localStorage.setItem(TEM_NOTIFICACAO, btoa(JSON.stringify(value)));
    }
  },
  actions: {
    async [LOGOUT]({ commit }) {
      clearAutoRefreshTimer();
      commit(MUTATE_TOKEN, undefined);
      commit(MUTATE_USER, null); // null para usar com JSON.parse
      commit(MUTATE_MENU, null); // null para usar com JSON.parse
      commit(MUTATE_ABILITIES, null); // null para usar com JSON.parse
      commit(MUTATE_TEM_NOTIFICACAO, false);

      await pararNotificacoes()
    },
    async [FORCE_LOGOUT]({ dispatch }, metadata = {}) {
      if (metadata?.reason) {
        console.warn("Sessao encerrada automaticamente.", metadata.reason);
      }

      await dispatch(LOGOUT);
      redirectToLogin();
    },
    async [LOGIN]({ commit, dispatch }, data) {
      const valid = data && data.token;

      if (valid) {
        clearAutoRefreshTimer();
        commit(MUTATE_TOKEN, data.token);
        commit(MUTATE_USER, {
          username: data.nomeDeUsuario,
          firstName: data.primeiroNome,
          lastName: data.sobrenome,
          fullName: data.nomeCompleto,
          email: data.email,
          roles: data.perfis,
          acceptedTerms: data.aceitouTermo,
          changePassword: data.trocarSenha,
          usuarioTipo: data.usuarioTipo,
          isAdmGre:  data.isAdmGre,
          isAdmGeral: data.isAdmGeral,
          firstAccess: data.primeiroAcesso,
          formacoesPendentes: data.formacoesPendentes,
        });

        if (data.notificacaoPendente) {
          commit(MUTATE_TEM_NOTIFICACAO, true);
        } else {
          commit(MUTATE_TEM_NOTIFICACAO, false);
        }

        dispatch(AUTO_REFRESH_TOKEN);

        await iniciarNotificacoes()
      } else {
        dispatch(FORCE_LOGOUT, { reason: "login_payload_invalido" });
      }
    },
    async [REFRESH_TOKEN]({ state, dispatch }) {
      if (!state.token) {
        console.warn("Tentativa de refresh token sem usuário logado")
        return;
      }

      try {
        const { data } = await axios
          .create({
            baseURL: configurations.api.baseUri,
            headers: {
              Authorization: `Bearer ${state.token}`
            }
          })
          .get("/v1/Acesso/RenovarToken");

        if (!data?.token) {
          console.error("Resposta de renovacao de token invalida.", data);
          await dispatch(FORCE_LOGOUT, { reason: "refresh_sem_token" });
          return;
        }

        await dispatch(LOGIN, data);
      } catch (error) {
        console.error("Nao foi possivel atualizar o token de autenticacao.", error?.response || error);
        await dispatch(FORCE_LOGOUT, { reason: "refresh_falhou" });
      }
    },
    async [UPDATE_MENU]({ commit }, data) {
      commit(MUTATE_MENU, data || null); // null para usar com JSON.parse
    },
    async [UPDATE_ABILITIES]({ commit }, data) {
      commit(MUTATE_ABILITIES, data || null); // null para usar com JSON.parse
    },
    async [AUTO_REFRESH_TOKEN]({ state, dispatch }) {
      if (!state.token) {
        return;
      }

      clearAutoRefreshTimer();

      try {
        const { exp } = jwtDecode(state.token);
        const expiration = dayjs(new Date(exp * 1000));
        const delayToRefresh = expiration.diff(dayjs(), "millisecond") - (15 * 60 * 1000);
        const safeDelay = Math.max(delayToRefresh, 0);

        autoRefreshTimeoutId = setTimeout(() => dispatch(REFRESH_TOKEN), safeDelay);
      } catch (error) {
        console.error("Falha ao agendar renovacao de token.", error);
        await dispatch(FORCE_LOGOUT, { reason: "token_invalido" });
      }
    },
    async [CLEAR]({ dispatch }) {
      dispatch(LOGOUT);
    },
    async [SETAR_NOTIFICACAO]({ commit }) {
      commit(MUTATE_TEM_NOTIFICACAO, true);
    },
    async [LIMPAR_NOTIFICACAO]({ commit }) {
      commit(MUTATE_TEM_NOTIFICACAO, false);
    },
  },
  getters: {
    [TOKEN]: (state) => state.token,
    [USER]: (state) => state.user,
    [MENU]: (state) => state.menu,
    [ABILITIES]: (state) => state.abilities,
    [TEM_NOTIFICACAO]: (state) => state.temNotificacao,
  },
};